import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Package, Plus, Search, Upload, Download, RefreshCw, Edit, Loader2, Check, TrendingDown, TrendingUp, AlertTriangle, ArrowUpDown, ClipboardCheck, X } from 'lucide-react';
import { getProductsWithBalance, createProduct, updateProduct, deleteProduct, importProducts, exportProducts, bulkStocktake } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A–Z)' },
  { value: 'name_desc', label: 'Name (Z–A)' },
  { value: 'sku_asc', label: 'SKU (A–Z)' },
  { value: 'balance_asc', label: 'Balance (Low → High)' },
  { value: 'balance_desc', label: 'Balance (High → Low)' },
];

const PRODUCTS_SORT_KEY = 'stockops_products_sort';

const ProductsPage = () => {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState(() => {
    return localStorage.getItem(PRODUCTS_SORT_KEY) || 'name_asc';
  });
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Bulk stocktake state
  const [bulkStocktakeMode, setBulkStocktakeMode] = useState(false);
  const [bulkStocktakeItems, setBulkStocktakeItems] = useState({});
  const [showBulkConfirmDialog, setShowBulkConfirmDialog] = useState(false);
  const [submittingBulk, setSubmittingBulk] = useState(false);

  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    barcode: '',
    image_url: '',
    restockable: true,
    active: true,
    stocktake_qty: '',
    low_stock_threshold: '',
  });

  const fetchProducts = useCallback(async () => {
    try {
      const res = await getProductsWithBalance();
      setProducts(res.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Persist sort option
  useEffect(() => {
    localStorage.setItem(PRODUCTS_SORT_KEY, sortOption);
  }, [sortOption]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let result = [...products];

    if (!showInactive) {
      result = result.filter((p) => p.active);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.sku.toLowerCase().includes(query) ||
          p.name.toLowerCase().includes(query) ||
          (p.barcode && p.barcode.includes(query))
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sortOption) {
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'sku_asc':
          return a.sku.localeCompare(b.sku);
        case 'balance_asc': {
          const aBalance = a.live_balance ?? Infinity;
          const bBalance = b.live_balance ?? Infinity;
          return aBalance - bBalance;
        }
        case 'balance_desc': {
          const aBalance = a.live_balance ?? -Infinity;
          const bBalance = b.live_balance ?? -Infinity;
          return bBalance - aBalance;
        }
        default:
          return 0;
      }
    });

    return result;
  }, [products, searchQuery, sortOption]);

  // Bulk stocktake helpers
  const bulkItemsCount = Object.keys(bulkStocktakeItems).length;
  
  const handleBulkStocktakeChange = (productId, value) => {
    if (value === '' || value === null) {
      const newItems = { ...bulkStocktakeItems };
      delete newItems[productId];
      setBulkStocktakeItems(newItems);
    } else {
      setBulkStocktakeItems({
        ...bulkStocktakeItems,
        [productId]: parseInt(value) || 0
      });
    }
  };

  const handleCancelBulkStocktake = () => {
    setBulkStocktakeMode(false);
    setBulkStocktakeItems({});
  };

  const handleSubmitBulkStocktake = async () => {
    setSubmittingBulk(true);
    try {
      const items = Object.entries(bulkStocktakeItems).map(([product_id, stocktake_qty]) => ({
        product_id,
        stocktake_qty
      }));
      
      const res = await bulkStocktake(items);
      
      toast.success(`Stocktake complete: ${res.data.updated_count} products updated`);
      if (res.data.errors.length > 0) {
        toast.warning(`${res.data.errors.length} errors occurred`);
      }
      
      setBulkStocktakeMode(false);
      setBulkStocktakeItems({});
      setShowBulkConfirmDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk stocktake failed');
    } finally {
      setSubmittingBulk(false);
    }
  };

  const resetForm = () => {
    setFormData({
      sku: '',
      name: '',
      barcode: '',
      image_url: '',
      restockable: true,
      active: true,
      stocktake_qty: '',
      low_stock_threshold: '',
    });
  };

  const handleOpenAdd = () => {
    resetForm();
    setEditingProduct(null);
    setShowAddDialog(true);
  };

  const handleOpenEdit = (product) => {
    setFormData({
      sku: product.sku,
      name: product.name,
      barcode: product.barcode || '',
      image_url: product.image_url || '',
      restockable: product.restockable,
      active: product.active,
      stocktake_qty: product.stocktake_qty !== null && product.stocktake_qty !== undefined ? String(product.stocktake_qty) : '',
      low_stock_threshold: product.low_stock_threshold !== null && product.low_stock_threshold !== undefined ? String(product.low_stock_threshold) : '',
    });
    setEditingProduct(product);
    setShowAddDialog(true);
  };

  const handleSave = async () => {
    if (!formData.sku.trim() || !formData.name.trim()) {
      toast.error('SKU and Name are required');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: formData.name,
        barcode: formData.barcode || null,
        image_url: formData.image_url || null,
        restockable: formData.restockable,
        active: formData.active,
      };

      if (formData.stocktake_qty !== '') {
        updateData.stocktake_qty = parseInt(formData.stocktake_qty) || 0;
      }

      if (formData.low_stock_threshold !== '') {
        updateData.low_stock_threshold = parseInt(formData.low_stock_threshold) || 0;
      } else {
        updateData.low_stock_threshold = null;
      }

      if (editingProduct) {
        await updateProduct(editingProduct.id, updateData);
        toast.success('Product updated');
      } else {
        await createProduct({
          ...formData,
          barcode: formData.barcode || null,
          image_url: formData.image_url || null,
          stocktake_qty: formData.stocktake_qty !== '' ? parseInt(formData.stocktake_qty) || 0 : null,
          low_stock_threshold: formData.low_stock_threshold !== '' ? parseInt(formData.low_stock_threshold) || 0 : null,
        });
        toast.success('Product created');
      }
      setShowAddDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingProduct) return;
    const confirmed = window.confirm(`Delete "${editingProduct.name}"? This will hide it from active lists.`);
    if (!confirmed) return;

    setDeleting(true);
    try {
      await deleteProduct(editingProduct.id);
      toast.success('Product deleted');
      setShowAddDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const res = await importProducts(file);
      toast.success(`Imported: ${res.data.created} created, ${res.data.updated} updated`);
      if (res.data.errors.length > 0) {
        toast.warning(`${res.data.errors.length} errors occurred`);
      }
      fetchProducts();
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportProducts();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const getBalanceDisplay = (product) => {
    if (product.live_balance === null || product.live_balance === undefined) {
      return { value: '—', color: 'text-muted-foreground', icon: null, isLowStock: false };
    }
    
    const balance = product.live_balance;
    const threshold = product.low_stock_threshold;
    const isLowStock = threshold !== null && threshold !== undefined && balance <= threshold;
    
    let color = 'text-success';
    let icon = null;
    
    if (balance <= 0) {
      color = 'text-destructive';
    } else if (isLowStock) {
      color = 'text-amber-600';
    } else if (balance <= 5) {
      color = 'text-amber-600';
    }
    
    const stocktake = product.stocktake_qty;
    if (stocktake !== null && stocktake !== undefined) {
      if (balance < stocktake) {
        icon = <TrendingDown className="w-3 h-3" />;
      } else if (balance > stocktake) {
        icon = <TrendingUp className="w-3 h-3" />;
      }
    }
    
    return { value: balance, color, icon, isLowStock };
  };

  const getRestockHint = (product) => {
    if (!isAdmin()) return null;
    
    const delta = product.restock_delta;
    if (delta === null || delta === undefined) return null;
    
    if (delta > 0) {
      return `Needs restock: ${delta}`;
    } else if (delta < 0) {
      return `Since last restock: ${delta}`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-enter" data-testid="products-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-heading font-bold">Products</h1>
          <p className="text-sm text-muted-foreground">{products.length} total</p>
        </div>
        <div className="flex gap-2">
          {isAdmin() && !bulkStocktakeMode && (
            <button
              onClick={() => setBulkStocktakeMode(true)}
              className="btn-icon"
              aria-label="Bulk Stocktake"
              title="Bulk Stocktake"
              data-testid="bulk-stocktake-btn"
            >
              <ClipboardCheck className="w-5 h-5" />
            </button>
          )}
          <label className="btn-icon cursor-pointer" data-testid="import-products-btn">
            <input
              type="file"
              accept=".csv"
              onChange={handleImport}
              className="hidden"
              disabled={importing || bulkStocktakeMode}
            />
            {importing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Upload className="w-5 h-5" />
            )}
          </label>
          <button
            onClick={handleExport}
            disabled={exporting || bulkStocktakeMode}
            className="btn-icon"
            aria-label="Export"
            data-testid="export-products-btn"
          >
            {exporting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Bulk Stocktake Mode Banner */}
      {bulkStocktakeMode && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-4" data-testid="bulk-stocktake-mode">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-primary" />
              <span className="font-semibold text-primary">Bulk Stocktake Mode</span>
            </div>
            <button
              onClick={handleCancelBulkStocktake}
              className="text-muted-foreground hover:text-foreground"
              data-testid="cancel-bulk-stocktake"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Enter quantities for products you want to count. All updates will share the same timestamp.
          </p>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {bulkItemsCount} product{bulkItemsCount !== 1 ? 's' : ''} to update
            </span>
            <Button
              onClick={() => setShowBulkConfirmDialog(true)}
              disabled={bulkItemsCount === 0}
              size="sm"
              data-testid="submit-bulk-stocktake"
            >
              <Check className="w-4 h-4 mr-1" />
              Apply Stocktake
            </Button>
          </div>
        </div>
      )}

      {/* Search & Sort */}
      {!bulkStocktakeMode && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-bar"
              data-testid="products-search-input"
            />
          </div>
          <div className="flex items-center gap-2 sm:ml-auto">
            {isAdmin() && (
              <>
                <Label htmlFor="show-inactive" className="text-xs text-muted-foreground">
                  Show inactive
                </Label>
                <Switch
                  id="show-inactive"
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                  data-testid="products-show-inactive"
                />
              </>
            )}
            <Select value={sortOption} onValueChange={setSortOption}>
              <SelectTrigger className="w-[140px] h-12" data-testid="products-sort-select">
                <ArrowUpDown className="w-4 h-4 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin() && (
              <Button
                onClick={handleOpenAdd}
                className="h-12 w-12 rounded-xl"
                data-testid="add-product-btn"
              >
                <Plus className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Products List */}
      {filteredAndSortedProducts.length === 0 ? (
        <div className="empty-state py-12">
          <Package className="empty-state-icon" />
          <h3 className="empty-state-title">No Products</h3>
          <p className="empty-state-text">
            {searchQuery ? 'No products match your search' : 'Add products to get started'}
          </p>
        </div>
      ) : (
        <div 
          className="space-y-2" 
          data-testid="products-list"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {filteredAndSortedProducts.map((product) => {
            const balanceDisplay = getBalanceDisplay(product);
            const restockHint = getRestockHint(product);
            
            return (
              <div
                key={product.id}
                className={`item-card flex items-center gap-3 ${!product.active ? 'opacity-60' : ''}`}
                data-testid={`product-${product.sku}`}
              >
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-12 h-12 rounded-lg object-cover bg-slate-100"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-slate-400" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    {!product.restockable && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 whitespace-nowrap">
                        Non-restockable
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground font-data">{product.sku}</p>
                </div>

                {/* Bulk Stocktake Input */}
                {bulkStocktakeMode ? (
                  <div className="w-24">
                    <Input
                      type="number"
                      min="0"
                      placeholder="Qty"
                      value={bulkStocktakeItems[product.id] ?? ''}
                      onChange={(e) => handleBulkStocktakeChange(product.id, e.target.value)}
                      className="h-10 text-center font-data"
                      data-testid={`bulk-qty-${product.sku}`}
                    />
                  </div>
                ) : (
                  <>
                    {/* Live Balance Display */}
                    <div className="text-right mr-2" data-testid={`balance-${product.sku}`}>
                      <div className={`flex items-center justify-end gap-1 font-data font-bold ${balanceDisplay.color}`}>
                        {balanceDisplay.isLowStock && (
                          <AlertTriangle className="w-3 h-3 text-amber-500" />
                        )}
                        {balanceDisplay.icon}
                        <span>{balanceDisplay.value}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Balance</p>
                      {restockHint && (
                        <p className="text-[10px] text-muted-foreground/70 mt-0.5">{restockHint}</p>
                      )}
                    </div>

                    {isAdmin() && (
                      <button
                        onClick={() => handleOpenEdit(product)}
                        className="btn-icon w-10 h-10"
                        aria-label="Edit product"
                        data-testid={`edit-product-${product.sku}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="sku">SKU *</Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                className="mt-1"
                disabled={!!editingProduct}
                data-testid="product-sku-input"
              />
            </div>

            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1"
                data-testid="product-name-input"
              />
            </div>

            <div>
              <Label htmlFor="barcode">Barcode</Label>
              <Input
                id="barcode"
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                className="mt-1 font-data"
                data-testid="product-barcode-input"
              />
            </div>

            <div>
              <Label htmlFor="image_url">Image URL</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="mt-1"
                placeholder="https://..."
                data-testid="product-image-input"
              />
            </div>

            <div>
              <Label htmlFor="stocktake_qty">Stocktake (Sets Current Balance)</Label>
              <Input
                id="stocktake_qty"
                type="number"
                min="0"
                value={formData.stocktake_qty}
                onChange={(e) => setFormData({ ...formData, stocktake_qty: e.target.value })}
                className="mt-1 font-data"
                placeholder="Leave empty if no stocktake"
                data-testid="product-stocktake-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter a value to set the current balance. Future movements will adjust from this point.
              </p>
            </div>

            <div>
              <Label htmlFor="low_stock_threshold">Low Stock Threshold (Optional)</Label>
              <Input
                id="low_stock_threshold"
                type="number"
                min="0"
                value={formData.low_stock_threshold}
                onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                className="mt-1 font-data"
                placeholder="e.g., 5"
                data-testid="product-threshold-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Show warning when balance falls to or below this level.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="restockable">Restockable</Label>
              <Switch
                id="restockable"
                checked={formData.restockable}
                onCheckedChange={(checked) => setFormData({ ...formData, restockable: checked })}
                data-testid="product-restockable-switch"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="active">Active</Label>
              <Switch
                id="active"
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                data-testid="product-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <div className="flex gap-2 w-full">
              {editingProduct && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 border-destructive text-destructive hover:text-destructive"
                  data-testid="delete-product-btn"
                >
                  {deleting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <X className="w-4 h-4 mr-1" />
                      Delete
                    </>
                  )}
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 btn-primary-action"
                data-testid="save-product-btn"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    <span>{editingProduct ? 'Update' : 'Create'} Product</span>
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Stocktake Confirmation Dialog */}
      <Dialog open={showBulkConfirmDialog} onOpenChange={setShowBulkConfirmDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-primary" />
              Confirm Bulk Stocktake
            </DialogTitle>
            <DialogDescription>
              You are about to update the stocktake for <strong>{bulkItemsCount} product{bulkItemsCount !== 1 ? 's' : ''}</strong>. 
              All products will share the same batch timestamp. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-2 max-h-48 overflow-y-auto">
            <div className="space-y-1">
              {Object.entries(bulkStocktakeItems).map(([productId, qty]) => {
                const product = products.find(p => p.id === productId);
                return product ? (
                  <div key={productId} className="flex justify-between text-sm py-1 border-b border-border/50">
                    <span className="truncate mr-2">{product.name}</span>
                    <span className="font-data font-medium">{qty}</span>
                  </div>
                ) : null;
              })}
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBulkConfirmDialog(false)}
              className="flex-1"
              data-testid="cancel-bulk-confirm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitBulkStocktake}
              disabled={submittingBulk}
              className="flex-1"
              data-testid="confirm-bulk-stocktake"
            >
              {submittingBulk ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Confirm
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
